import { Alert, AlertDescription, Card, CardContent } from '@design-system';
import { isHybridConsumerQuote, paymentScheduleOfQuote } from '@shared/consumerPlan';
import { CoverageDetailsSection } from '../components/coverage-details-section';
import { Figure, MaxBenefitsAtAge, metricFigureClassName } from '../components/figures';
import { PolicyReviewInProgressNotice } from '../components/policy-review-in-progress-notice';
import { ScenarioSummary } from '../components/scenario-summary';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { usePlanTotals } from '../hooks/use-plan-totals';
import { GUARANTEE_CONDITIONS } from '../lib/copy';
import { money } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

const SCHEDULE_HELP: Record<ConsumerPaymentSchedule, string> = {
  tenPay: 'You pay for 10 years. Coverage starts when the policy takes effect and continues afterward under its terms.',
  singlePay: 'You make one premium payment when the policy takes effect.',
  ongoing: 'Ongoing premiums may change.',
};

export function CoverageStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { selectedQuote, selectedProjection, scenario, summaryStep } = usePlanTotals(clientId);
  if (!selectedQuote || !selectedProjection || !scenario) return null;

  const paysBeneficiaries = isHybridConsumerQuote(selectedQuote) && selectedProjection.deathBenefitWithoutCare > 0;
  const schedule = paymentScheduleOfQuote(selectedQuote) ?? 'ongoing';

  return (
    <StepFrame
      step="coverage"
      title="Does this coverage work for you?"
      subtitle={<ScenarioSummary scenario={scenario} variant="sentence" />}
      onBack={() => back('options')}
      help={
        <>
          <p>
            The <strong>maximum</strong> is a limit, not a promise that all of it will be used. Amounts for
            beneficiaries are what the policy can pay at death, separate from the care benefit.
          </p>
          <p>{SCHEDULE_HELP[schedule]}</p>
        </>
      }
      primary={{ label: 'Use this coverage', onClick: () => goTo(summaryStep) }}
      secondary={{ label: 'Choose another option', onClick: () => back('options') }}
      next="see how the whole care event comes together, then apply."
      footnote={
        <>
          {paysBeneficiaries && `Beneficiary amounts are alternative outcomes, not added together. ${GUARANTEE_CONDITIONS} `}
          Projected care payments are estimates.
        </>
      }
    >
      <PolicyReviewInProgressNotice clientId={clientId} />
      <SelectedCoverageSummary quote={selectedQuote} />
      <Card className="py-4">
        <CardContent className="px-4">
          <MaxBenefitsAtAge
            age={scenario.careStartAge}
            monthlyMax={selectedProjection.monthlyMaxAtCareStart}
            totalMax={selectedProjection.maxBenefitAtCareStart}
            columns={paysBeneficiaries ? 4 : 3}
          >
            {paysBeneficiaries ? (
              <>
                <Figure
                  label="Guaranteed paid to beneficiaries upon death"
                  value={`at least ${money(selectedProjection.guaranteedDeathBenefitAfterCare)}`}
                  className={metricFigureClassName}
                />
                <Figure
                  label="Death benefit if no care is needed"
                  value={money(selectedProjection.deathBenefitWithoutCare)}
                  className={metricFigureClassName}
                />
              </>
            ) : (
              <Figure label="Benefit for beneficiaries" value="None" className={metricFigureClassName} />
            )}
          </MaxBenefitsAtAge>
        </CardContent>
      </Card>
      <CoverageDetailsSection quote={selectedQuote} projection={selectedProjection} scenario={scenario} />

      <Alert variant="info">
        <AlertDescription>
          <strong>Coming up:</strong> how this policy pays across the whole care event and what you’d cover
          yourself, with the option to apply right away.
        </AlertDescription>
      </Alert>
    </StepFrame>
  );
}
